import React, { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  HStack,
  VStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';

/**
 * ModernDetailsTable - A modern, reusable scrollable data table component for dashboards
 * 
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Column configuration array with { id, name, selector, sortable, format, cell, width, align }
 * @param {Function} onRowClick - Function called when a row is clicked
 * @param {string} accentColor - Theme accent color (e.g., 'blue', 'green', 'purple')
 * @param {number} defaultSortColumnId - ID of the column to sort by default
 * @param {boolean} defaultSortAsc - Default sort direction (true = ascending)
 * @param {string} searchPlaceholder - Placeholder text for search input
 * @param {boolean} showSearch - Whether to show the search input
 * @param {number} visibleRows - Number of rows visible before scrolling (default 12)
 * @param {number} rowHeight - Height of each row in pixels (default 48)
 */
const ModernDetailsTable = ({
  data = [],
  columns = [],
  onRowClick,
  accentColor = 'blue',
  defaultSortColumnId = 1,
  defaultSortAsc = false,
  searchPlaceholder = 'Search businesses...',
  showSearch = true,
  visibleRows = 12,
  rowHeight = 48,
}) => {
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.750');
  const rowHoverBg = useColorModeValue(`${accentColor}.50`, `${accentColor}.900`);
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const searchBg = useColorModeValue('white', 'gray.700');
  const evenRowBg = useColorModeValue('gray.25', 'gray.850');

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(defaultSortColumnId);
  const [sortAsc, setSortAsc] = useState(defaultSortAsc);

  // Calculate max height for scrollable area
  const maxHeight = visibleRows * rowHeight;

  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const query = searchQuery.toLowerCase();
    return data.filter((row) => {
      return columns.some((col) => {
        const value = col.selector ? col.selector(row) : '';
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [data, searchQuery, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    const column = columns.find((col) => col.id === sortColumn);
    if (!column || !column.selector) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = column.selector(a);
      const bVal = column.selector(b);

      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortAsc ? aVal - bVal : bVal - aVal;
      }

      // Handle strings
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      if (sortAsc) {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  }, [filteredData, sortColumn, sortAsc, columns]);

  // Handle sort
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(columnId);
      setSortAsc(false);
    }
  };

  // Get sort icon
  const getSortIcon = (columnId) => {
    if (sortColumn !== columnId) return FaSort;
    return sortAsc ? FaSortUp : FaSortDown;
  };

  // Render cell content
  const renderCell = (column, row, rowIndex) => {
    if (column.cell) {
      return column.cell(row, rowIndex);
    }
    
    const value = column.selector ? column.selector(row) : '';
    
    if (column.format) {
      return column.format(row);
    }
    
    return value;
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      overflow="hidden"
      shadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
    >
      {/* Header with Search and Stats */}
      <Flex
        justify="space-between"
        align="center"
        p={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bg={headerBg}
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={4}>
          <Badge
            colorScheme={accentColor}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="medium"
          >
            {sortedData.length} {sortedData.length === 1 ? 'result' : 'results'}
          </Badge>
        </HStack>

        {showSearch && (
          <InputGroup maxW="300px" size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color={mutedTextColor} boxSize={3} />
            </InputLeftElement>
            <Input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={searchBg}
              borderRadius="lg"
              borderColor={borderColor}
              _focus={{
                borderColor: `${accentColor}.400`,
                boxShadow: `0 0 0 1px var(--chakra-colors-${accentColor}-400)`,
              }}
              _placeholder={{ color: mutedTextColor }}
            />
          </InputGroup>
        )}
      </Flex>

      {/* Table with fixed header and scrollable body */}
      <Box position="relative">
        {/* Fixed Header */}
        <Box overflowX="auto">
          <Box as="table" width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <Box as="thead">
              <Box as="tr" bg={headerBg}>
                {columns.map((column, colIndex) => (
                  <Box
                    as="th"
                    key={column.id || colIndex}
                    px={4}
                    py={3}
                    textAlign={column.align || (column.right ? 'right' : 'left')}
                    fontWeight="semibold"
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    color={mutedTextColor}
                    cursor={column.sortable !== false ? 'pointer' : 'default'}
                    onClick={() => column.sortable !== false && handleSort(column.id)}
                    _hover={column.sortable !== false ? { color: `${accentColor}.500` } : {}}
                    transition="color 0.2s"
                    width={column.width}
                    minWidth={column.minWidth}
                    borderBottomWidth="2px"
                    borderColor={borderColor}
                  >
                    <HStack
                      spacing={1}
                      justify={column.align || (column.right ? 'flex-end' : 'flex-start')}
                    >
                      <Text>{column.name}</Text>
                      {column.sortable !== false && (
                        <Icon
                          as={getSortIcon(column.id)}
                          boxSize={3}
                          color={sortColumn === column.id ? `${accentColor}.500` : 'gray.400'}
                        />
                      )}
                    </HStack>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Body */}
        <Box 
          overflowY="auto" 
          overflowX="auto"
          maxH={`${maxHeight}px`}
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-gray-300)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'var(--chakra-colors-gray-400)',
            },
          }}
        >
          <Box as="table" width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <Box as="tbody">
              {sortedData.length === 0 ? (
                <Box as="tr">
                  <Box
                    as="td"
                    colSpan={columns.length}
                    textAlign="center"
                    py={12}
                    color={mutedTextColor}
                  >
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="medium">No results found</Text>
                      <Text fontSize="sm">Try adjusting your search criteria</Text>
                    </VStack>
                  </Box>
                </Box>
              ) : (
                sortedData.map((row, rowIndex) => (
                  <Box
                    as="tr"
                    key={row.maps_uri || row.id || rowIndex}
                    bg={rowIndex % 2 === 0 ? 'transparent' : evenRowBg}
                    cursor={onRowClick ? 'pointer' : 'default'}
                    onClick={() => onRowClick && onRowClick(row)}
                    _hover={{
                      bg: rowHoverBg,
                      '& td:first-of-type': {
                        borderLeftColor: `${accentColor}.500`,
                      },
                    }}
                    transition="all 0.15s ease"
                    role="group"
                    h={`${rowHeight}px`}
                  >
                    {columns.map((column, colIndex) => (
                      <Box
                        as="td"
                        key={column.id || colIndex}
                        px={4}
                        py={3}
                        textAlign={column.align || (column.right ? 'right' : 'left')}
                        fontSize="sm"
                        color={textColor}
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                        borderLeftWidth={colIndex === 0 ? '3px' : '0'}
                        borderLeftColor="transparent"
                        transition="border-color 0.15s ease"
                        fontWeight={colIndex === 0 ? 'medium' : 'normal'}
                        width={column.width}
                        minWidth={column.minWidth}
                      >
                        {renderCell(column, row, rowIndex)}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Helper components for common cell types
 */

// Trend indicator component
export const TrendCell = ({ value, threshold = 0, positiveColor = 'green', negativeColor = 'red' }) => {
  const isPositive = value > threshold;
  const color = isPositive ? positiveColor : value < threshold ? negativeColor : 'gray';
  const icon = isPositive ? FaArrowUp : value < threshold ? FaArrowDown : null;

  return (
    <HStack spacing={1} justify="flex-end">
      <Text fontWeight="medium">{value.toLocaleString()}</Text>
      {icon && (
        <Icon
          as={icon}
          color={`${color}.500`}
          boxSize={3}
        />
      )}
    </HStack>
  );
};

// Badge cell component
export const BadgeCell = ({ value, colorScheme = 'blue' }) => (
  <Badge
    colorScheme={colorScheme}
    borderRadius="full"
    px={2}
    py={0.5}
    fontSize="xs"
    fontWeight="medium"
  >
    {value}
  </Badge>
);

// Formatted number cell
export const NumberCell = ({ value, suffix = '', prefix = '' }) => (
  <Text fontWeight="medium" fontFeatureSettings="'tnum' 1">
    {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
  </Text>
);

// Progress cell
export const ProgressCell = ({ value, max, colorScheme = 'blue' }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const progressBg = useColorModeValue('gray.100', 'gray.700');
  
  return (
    <HStack spacing={3} minW="120px">
      <Box flex="1" h="6px" bg={progressBg} borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          w={`${Math.min(percentage, 100)}%`}
          bg={`${colorScheme}.400`}
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
      <Text fontSize="sm" fontWeight="medium" w="40px" textAlign="right">
        {value.toLocaleString()}
      </Text>
    </HStack>
  );
};

export default ModernDetailsTable;
